import { FilterConfig } from "./services/filter-url-sync.service";
import {
  ANALYTICS_FILTER_CONFIG,
  KPI_FILTER_CONFIG,
  MAP_LIST_FILTER_CONFIG
} from "./services/filter-sync-configs";

/**
 * Everything the app has to know about one top-bar segment. Before this table the
 * six segments were described in five places that had to agree with each other:
 * the route tree, six hand-written `<a mat-tab-link>` blocks, `TAB_FILTER_CONFIGS`,
 * the reuse strategy's `component === MapComponent || component === ListComponent`,
 * and the four copies of the landing segment `"kpi"`. Adding a seventh tab meant
 * finding all five; forgetting one produced a tab that renders but drops its
 * filters, or a URL the guard sends home while the tab still shows.
 *
 * A field here is data every reader can act on. What stays out is per-tab chrome:
 * map's loading spinner, analytics' close affordance and manager's spinner are
 * markup, and they live in the template as slots keyed on the segment.
 */
export interface TabDefinition {
  /** First path segment, and the whole of the tab's `routerLink` (`/map`). */
  readonly segment: string;
  /** i18n key of the label, used for both the text and the aria-label. */
  readonly labelKey: string;
  /** Web-tracking id, on the anchor and in the click handler. */
  readonly trackingId: string;
  /**
   * Minimum profile level (a UserRightsService `RIGHT_LIST` entry) needed to
   * reach the tab. Read twice from here — the dashboard hides the tab, and
   * `roleGuard` refuses the URL through `data.requiredRole` — so the two can no
   * longer disagree. Absent = open to every signed-in user, which is why the
   * routes of the tabs that declare nothing here carry no `roleGuard` at all: a
   * tab that gains a role needs the guard added to its route with it.
   */
  readonly requiredRole?: string;
  /**
   * The page's URL⇄filter config, when it has one. The URL is authoritative for
   * filters (see FilterUrlSyncService), so a bare `routerLink="/list"` would land
   * on the target with an empty query string and switch every filter off: the
   * dashboard gives each link the subset of the current params its own target
   * reads. Absent = the page has no URL-synced filters (manager, incident), and
   * its link stays bare.
   */
  readonly filterConfig?: FilterConfig;
  /**
   * The segment has a record sibling pointing at the same component
   * (`/map` ↔ `/map/tracker/:id`), so the router must keep the instance alive
   * across that hop instead of tearing down the map or the loaded rows. Read by
   * TabDetailRouteReuseStrategy off `route.data`.
   */
  readonly reuseAcrossDetail?: boolean;
  /**
   * Tag shared by the segments whose record sub-paths mean the same thing, so the
   * top bar carries the sub-path across them: from `/map/site/88` the List link is
   * `/list/site/88`, not `/list`. Map and list are one such pair - they address the
   * same fleet, one as pins and one as rows - and the tag is what says so, in one
   * place, rather than each side listing the sub-paths the other must also list.
   *
   * Absent = the tab's links are always its bare root, which is what a tab with no
   * counterpart wants (a `/manager/users/:user_id` means nothing under `/incident`).
   */
  readonly recordShape?: string;
  /** The tab an unknown, refused or bare URL lands on. Exactly one has it. */
  readonly isLanding?: boolean;
}

/**
 * The top-bar segments, in the order they are shown.
 *
 * `PROVISIONING` is deliberately absent: `userAccess` still names it (it is a tab
 * identifier in navigation payloads) but no route or tab has ever existed for it.
 */
export const TAB_SEGMENTS: readonly TabDefinition[] = [
  {
    segment: "kpi",
    labelKey: "dashboard.kpi",
    trackingId: "tab-kpi",
    filterConfig: KPI_FILTER_CONFIG,
    isLanding: true
  },
  {
    segment: "map",
    labelKey: "dashboard.map",
    trackingId: "tab-map",
    filterConfig: MAP_LIST_FILTER_CONFIG,
    reuseAcrossDetail: true,
    recordShape: "fleet"
  },
  {
    segment: "list",
    labelKey: "dashboard.list",
    trackingId: "tab-list",
    filterConfig: MAP_LIST_FILTER_CONFIG,
    reuseAcrossDetail: true,
    recordShape: "fleet"
  },
  {
    segment: "analytics",
    labelKey: "dashboard.analytics",
    trackingId: "tab-analytics",
    requiredRole: "SuperUser",
    filterConfig: ANALYTICS_FILTER_CONFIG
  },
  {
    segment: "manager",
    labelKey: "dashboard.manager",
    trackingId: "tab-manager",
    requiredRole: "Admin"
  },
  {
    segment: "incident",
    labelKey: "dashboard.incident",
    trackingId: "tab-incident"
  }
];

/**
 * The `data` a segment's routes carry: what the route tree has to publish so the
 * guard and the reuse strategy can read it off a snapshot. Called from
 * app.routes.ts rather than written out there, so the table stays the only place
 * a role or a reuse flag is decided.
 * @param segment {string} The tab whose routes are being declared.
 * @returns {{ [key: string]: unknown }} The route `data` object (possibly empty).
 */
export function tabRouteData(segment: string): { [key: string]: unknown } {
  const tab: TabDefinition = TAB_SEGMENTS.find((t) => t.segment === segment);
  const data: { [key: string]: unknown } = {};
  if (tab?.requiredRole) {
    data["requiredRole"] = tab.requiredRole;
  }
  if (tab?.reuseAcrossDetail) {
    data["reuseAcrossDetail"] = true;
  }
  return data;
}

/**
 * Where a URL goes when it has nowhere else to go: the route tree's fall-through
 * redirect, both guards' rejection target, and the dashboard's idea of which tab
 * a bare URL shows.
 */
export const LANDING_SEGMENT: string = TAB_SEGMENTS.find(
  (tab) => tab.isLanding
).segment;

/** {@link LANDING_SEGMENT} as an absolute path, for `createUrlTree`. */
export const LANDING_PATH: string = `/${LANDING_SEGMENT}`;
